import { SKIPPED_CARD_ERROR_PREFIX } from "@juristic/types/models/chat";
import { UiDataPartType } from "@juristic/types/stream/ai/data_events";
import { ToolHookEventType } from "@juristic/types/stream/ai/tool_hooks";
import { styled } from "@mui/material/styles";
import { Button, RefreshIcon, Spinner, StatusDot } from "@UI";
import { isToolUIPart } from "ai";
import React from "react";
import { useTranslation } from "react-i18next";
import { WordFileCard, wordFileOf } from "./cards/WordFileCard";
import { MessageActions } from "./MessageActions";
import { SentAttachments } from "./SentAttachments";
import { ActivityFeed } from "./MessageParts/ActivityFeed";
import { isFeedPart, renderPart } from "./MessageParts/registry";
import { findCardInterruption } from "./utils/cardInterruption";
import { isCompacting, isReadingAttachments } from "./utils/compaction";
const Row = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "var(--j-sp-2)",
    minWidth: 0,
    position: "relative",
    "&[data-role=user]": {
        alignItems: "flex-end"
    }
});
const Note = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-sm)",
    color: "var(--j-ink-2)"
});
const RESOLVED_TYPES = new Set([
    ToolHookEventType.ConfirmationResolved,
    ToolHookEventType.ChoiceResolved
]);
const CARD_TYPES = new Set([
    ToolHookEventType.ConfirmationRequest,
    ToolHookEventType.ChoiceRequest
]);
const isCardResolved = (message, toolCallId)=>message.parts.some((part)=>part.type === UiDataPartType.ToolHook && RESOLVED_TYPES.has(part.data.type) && part.data.toolCallId === toolCallId);
function buildSegments(message, ctx) {
    const segments = [];
    message.parts.forEach((part, index)=>{
        const last = segments[segments.length - 1];
        if (isFeedPart(part)) {
            if (last?.kind === "feed") last.parts.push({
                part,
                index
            });
            else segments.push({
                kind: "feed",
                key: `feed-${index}`,
                parts: [
                    {
                        part,
                        index
                    }
                ]
            });
            const file = wordFileOf(part);
            if (file) segments.push({
                kind: "content",
                key: `file-${index}`,
                element: /*#__PURE__*/ React.createElement(WordFileCard, {
                    file: file
                })
            });
            return;
        }
        const element = renderPart(part, index, ctx);
        if (element) segments.push({
            kind: "content",
            key: `part-${index}`,
            element
        });
    });
    return segments;
}
function MessageItemComponent({ message, isStreaming, isLast, onToolConfirmationResponse, onStepsSpinning, onRetry, toolDecisions, respondingToolIds, choiceAnswers }) {
    const { t } = useTranslation();
    const ctx = {
        message,
        messageId: message.id,
        isStreaming,
        onToolConfirmationResponse,
        onStepsSpinning,
        toolDecisions,
        respondingToolIds,
        choiceAnswers
    };
    const segments = buildSegments(message, ctx);
    const showActions = message.role === "assistant" && !isStreaming;
    const isStoppedUnanswered = showActions && message.metadata?.status === "stopped" && !message.parts.some((part)=>part.type === "text" && part.text.trim() !== "") && !message.parts.some((part)=>isToolUIPart(part) && part.state === "output-error" && part.errorText.startsWith(SKIPPED_CARD_ERROR_PREFIX) || part.type === UiDataPartType.ToolHook && CARD_TYPES.has(part.data.type) && toolDecisions?.[part.data.toolCallId] === undefined && choiceAnswers?.[part.data.toolCallId] === undefined && !isCardResolved(message, part.data.toolCallId) && findCardInterruption(message, part.data.toolCallId) !== false);
    return /*#__PURE__*/ React.createElement(Row, {
        "data-role": message.role === "user" ? "user" : "assistant",
        className: "message"
    }, isStreaming && isCompacting(message.parts) && /*#__PURE__*/ React.createElement(Note, {
        role: "status"
    }, /*#__PURE__*/ React.createElement(Spinner, {
        size: "sm"
    }), /*#__PURE__*/ React.createElement("span", null, t("chat.compacting_conversation"))), isStreaming && isReadingAttachments(message.parts) && /*#__PURE__*/ React.createElement(Note, {
        role: "status"
    }, /*#__PURE__*/ React.createElement(Spinner, {
        size: "sm"
    }), /*#__PURE__*/ React.createElement("span", null, t("chat.attachments.reading"))), segments.map((segment, position)=>segment.kind === "feed" ? /*#__PURE__*/ React.createElement(ActivityFeed, {
            key: segment.key,
            parts: segment.parts,
            isActive: isStreaming && position === segments.length - 1,
            ctx: ctx
        }) : /*#__PURE__*/ React.createElement(React.Fragment, {
            key: segment.key
        }, segment.element)), message.role === "user" && /*#__PURE__*/ React.createElement(SentAttachments, {
        metadata: message.metadata
    }), isStoppedUnanswered && /*#__PURE__*/ React.createElement(Note, {
        role: "status"
    }, /*#__PURE__*/ React.createElement(StatusDot, {
        status: "idle"
    }), /*#__PURE__*/ React.createElement("span", null, t("chat.stopped")), onRetry && /*#__PURE__*/ React.createElement(Button, {
        variant: "ghost",
        size: "sm",
        startIcon: /*#__PURE__*/ React.createElement(RefreshIcon, {
            size: 14
        }),
        onClick: onRetry
    }, t("chat.retry"))), showActions && /*#__PURE__*/ React.createElement(MessageActions, {
        message: message,
        isPinned: isLast
    }));
}
export const MessageItem = /*#__PURE__*/ React.memo(MessageItemComponent);
